import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";

const ShopDescription = () => {
  const { id } = useParams();
  const { addToCart } = useCart();

  // --- State ---
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [added, setAdded] = useState(false);

  // --- API Endpoint ---
  const API_URL = `${process.env.REACT_APP_BACKEND}/api/products/${id}`;

  // --- Fetch Product ---
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(API_URL);
        setProduct(response.data);
        setError("");
      } catch (err) {
        console.error(err);
        setError("Unable to load product details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProduct();
  }, [id, API_URL]);

  // --- Quantity Handlers ---
  const handleQuantityChange = (amount) => {
    setQuantity((prev) => Math.max(1, prev + amount));
  };

  // --- Add to Cart (Using Context) ---
  const handleAddToCart = () => {
    if (!product) return;

    addToCart(product, quantity);

    setAdded(true);
    setTimeout(() => setAdded(false), 2500);

    setQuantity(1);
  };

  // --- Loading State ---
  if (loading) {
    return (
      <div
        className="page"
        style={{ textAlign: "center", padding: "120px 20px" }}
      >
        <p style={{ color: "#374151", fontSize: "1.1rem", fontWeight: "500" }}>
          Loading product details...
        </p>
      </div>
    );
  }

  // --- Error State ---
  if (error || !product) {
    return (
      <div
        className="page"
        style={{ textAlign: "center", padding: "120px 20px" }}
      >
        <p
          style={{
            color: "#dc2626",
            marginBottom: "20px",
            fontSize: "1.1rem",
            fontWeight: "500",
          }}
        >
          {error || "Product not found."}
        </p>
        <Link
          to="/shop"
          style={{
            color: "#111827",
            textDecoration: "underline",
            fontWeight: "600",
          }}
        >
          &larr; Return to Shop
        </Link>
      </div>
    );
  }

  return (
    <div
      className="page"
      style={{
        maxWidth: "1000px",
        margin: "120px auto 50px",
        padding: "20px",
      }}
    >
      {/* Back Link */}
      <div style={{ marginBottom: "24px" }}>
        <Link
          to="/shop"
          style={{
            color: "#374151",
            fontSize: "1rem",
            fontWeight: "600",
            textDecoration: "none",
          }}
        >
          &larr; Back to Collection
        </Link>
      </div>

      {/* Main Product Section */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "40px",
          alignItems: "start",
        }}
      >
        {/* Product Image */}
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: "12px",
            overflow: "hidden",
            minHeight: "350px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #e5e7eb",
          }}
        >
          {product.image ? (
            <img
              src={`${process.env.REACT_APP_BACKEND}/uploads/${product.image}`}
              alt={product.name}
              style={{
                width: "100%",
                maxHeight: "450px",
                objectFit: "cover",
                display: "block",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
                e.target.parentElement.innerHTML =
                  '<div style="font-size: 5rem; padding: 4rem 0;">🍯</div>';
              }}
            />
          ) : (
            <div style={{ fontSize: "5rem", padding: "4rem 0" }}>🍯</div>
          )}
        </div>

        {/* Product Details */}
        <div>
          <div
            className="eyebrow"
            style={{
              textTransform: "uppercase",
              letterSpacing: "1px",
              fontSize: "0.85rem",
              color: "#4b5563",
              fontWeight: "700",
            }}
          >
            BEELUXE COLLECTION
          </div>
          <h1
            style={{
              fontSize: "clamp(1.5rem, 5vw, 2.4rem)",
              lineHeight: "1.4",
              margin: "8px 0 12px 0",
              color: "#FFD700",
              fontWeight: "800",
              wordSpacing: "0.12em", // Adds space between words
              letterSpacing: "0.02em", // Adds slight breathing room between characters
              overflowWrap: "break-word", // Prevents long words from overflowing without breaking normal words
            }}
          >
            {product.name}
          </h1>

          <div
            className="price-tag"
            style={{
              fontSize: "1.75rem",
              fontWeight: "700",
              marginBottom: "20px",
              color: "white",
            }}
          >
            ${Number(product.price).toFixed(2)}
          </div>

          <p
            style={{
              lineHeight: "1.7",
              color: "#FFFFFF",
              fontSize: "1.05rem",
              marginBottom: "32px",
            }}
          >
            {product.description ||
              "Small-batch honey and hive goods, bottled within 48 hours of harvest."}
          </p>

          {/* Quantity Controls */}
          <div style={{ marginBottom: "28px" }}>
            <label
              style={{
                display: "block",
                fontSize: "0.85rem",
                fontWeight: "700",
                marginBottom: "10px",
                textTransform: "uppercase",
                color: "white",
                letterSpacing: "0.5px",
              }}
            >
              Quantity
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <button
                onClick={() => handleQuantityChange(-1)}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  border: "1px solid #9ca3af",
                  background: "#fff",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  color: "#111827",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                -
              </button>

              <span
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "700",
                  minWidth: "24px",
                  textAlign: "center",
                  color: "#FFFFFF",
                }}
              >
                {quantity}
              </span>

              <button
                onClick={() => handleQuantityChange(1)}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  border: "1px solid #9ca3af",
                  background: "#fff",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  color: "#111827",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                +
              </button>
            </div>
          </div>

          {/* Add to Cart Button */}
          <button
            onClick={handleAddToCart}
            style={{
              width: "100%",
              padding: "16px 24px",
              borderRadius: "30px",
              border: "none",
              backgroundColor: added ? "#059669" : "#111827",
              color: "#ffffff",
              fontSize: "1.05rem",
              fontWeight: "700",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            {added
              ? "✓ Added to Cart"
              : `Add to Cart — $${(Number(product.price) * quantity).toFixed(2)}`}
          </button>

          {/* Value Props */}
          <div
            style={{
              marginTop: "40px",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "24px",
            }}
          >
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                color: "#374151",
                fontSize: "0.95rem",
                fontWeight: "500",
                lineHeight: "2",
              }}
            >
              <li>✓ 100% Raw & Unfiltered</li>
              <li>✓ Sustainably harvested</li>
              <li>✓ Bottled within 48 hours</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopDescription;
